'use client';

import { useEffect, useState } from 'react';

import { useSupabase } from '@kit/supabase/hooks/use-supabase';

// First-purchase offer: coins doubled on the first pack bought within 24h of
// signup. Mirrors the server-side grant in the billing webhook
// (isFirstPurchaseOfferEligible / grantFirstPurchaseBonus) — the webhook has
// a 1h grace on top of this so a checkout started near the deadline still
// gets the bonus.
const FIRST_PURCHASE_WINDOW_MS = 24 * 60 * 60 * 1000;

export function formatCountdown(ms: number) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return [h, m, s].map((n) => String(n).padStart(2, '0')).join(':');
}

/**
 * Deadline of the user's first-purchase offer, or null when not eligible
 * (signed out, past 24h since signup, or has already paid). Evaluated when
 * `enabled` flips true; a 1s ticker drives the countdown while an offer is
 * live.
 */
export function useFirstPurchaseOffer(enabled: boolean) {
  const supabase = useSupabase();
  const [deadline, setDeadline] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;

    let cancelled = false;

    async function evaluate() {
      try {
        const { data: userData } = await supabase.auth.getUser();
        const createdAt = userData.user?.created_at;

        if (!createdAt) return;

        const end = new Date(createdAt).getTime() + FIRST_PURCHASE_WINDOW_MS;

        if (end <= Date.now()) return;

        // RLS lets users read their own trust row; no row = never paid.
        const { data: trust } = await supabase
          .from('account_trust')
          .select('has_successful_payment')
          .maybeSingle();

        if (trust?.has_successful_payment) return;

        if (!cancelled) setDeadline(end);
      } catch {
        // No offer on failure — everything works fine without it.
      }
    }

    void evaluate();

    return () => {
      cancelled = true;
    };
  }, [enabled, supabase]);

  // Tick only while an offer is displayed.
  useEffect(() => {
    if (!deadline) return;

    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);

  if (!deadline || deadline <= now) return null;

  return { remainingMs: deadline - now };
}
